import { DestroyRef, Injectable, computed, inject, resource, signal } from "@angular/core";
import { takeUntilDestroyed, toObservable } from "@angular/core/rxjs-interop";
import { isiFirmTax, productStringFromApplicationType } from "@app/views-interfaces";
import { AuthService } from "@shared/auth";
import { DashboardsService } from "@shared/dashboards-service";
import { IframeMessagesService } from "@shared/iframe-messages-service";
import { ResponseErrorHandlerService } from "@shared/response-error-handler-service";
import { firstValueFrom, filter, take } from "rxjs";

interface AvailableDocumentsResponse {
    dashboardId: string;
    dashboardCardIds: string[];
}

type AvailableDocuments = Set<string>;

interface AvailableDocumentsResource {
    applicationType: number | undefined;
    taxYear: number | null | undefined;
}

type DashboardIdAvailableDocumentMap = Map<string, AvailableDocuments>;
type DashboardYearAvailableDocumentMap = Map<number, DashboardIdAvailableDocumentMap>;
type DashboardTypeAvailableDocumentMap = Map<string, DashboardYearAvailableDocumentMap>;

@Injectable({ providedIn: "root" })
export class IfirmTaxCardsService {
    private readonly destroyRef = inject(DestroyRef);
    private readonly authService = inject(AuthService);
    private readonly dashboardsService = inject(DashboardsService);
    private readonly iframeMessagesService = inject(IframeMessagesService);
    private readonly responseErrorHandlerService = inject(ResponseErrorHandlerService);

    private isReloading = true;
    private refreshTimer = setInterval(() => this.reload(), 3600000);

    constructor() {
        this.destroyRef.onDestroy(() => clearInterval(this.refreshTimer));

        this.authService.sessionData$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => this.reload());
    }

    private reload() {
        if (this.authService.inReloginPrompted()) return;
        this.isReloading = true;
        this.availableDocumentsResource.reload();
    }

    private availableDocumentsResource = resource<DashboardIdAvailableDocumentMap | undefined, AvailableDocumentsResource>({
        params: () => ({
            applicationType: this.dashboardsService.currentDashboardType(),
            taxYear: this.dashboardsService.currentDashboardYear(),
        }),
        loader: async ({ params }) => {
            const { applicationType, taxYear } = params;
            if (!applicationType || !taxYear || !isiFirmTax(applicationType) || this.authService.inReloginPrompted()) return;

            const productType = productStringFromApplicationType(applicationType);
            if (!this.isReloading && this.availableDocuments().get(productType)?.get(taxYear)) {
                return this.availableDocuments().get(productType)?.get(taxYear);
            }

            this.isReloading = false;
            const documents = await this.getUserAvailableDocuments(productType, taxYear);
            if (documents?.length) {
                const dashboardYearMap: DashboardIdAvailableDocumentMap = new Map();
                for (const item of documents) {
                    dashboardYearMap.set(item.dashboardId, new Set(item.dashboardCardIds));
                }
                this.availableDocuments.update((availableDocuments) => {
                    const updated = new Map(availableDocuments);
                    const dashboardTypeMap: DashboardYearAvailableDocumentMap = updated.get(productType) ?? new Map();
                    dashboardTypeMap.set(taxYear, dashboardYearMap);
                    updated.set(productType, dashboardTypeMap);
                    return updated;
                });
                return dashboardYearMap;
            }
        },
    });
    private readonly availableDocuments$ = toObservable(this.availableDocumentsResource.value);

    private availableDocuments = signal<DashboardTypeAvailableDocumentMap>(new Map());

    public currentAvailableDocuments = computed(
        () => {
            const availableDocuments = this.availableDocumentsResource.value();
            const dashboardService = this.dashboardsService as any;
            const dashboardId = dashboardService.currentId?.() ?? dashboardService.sDashboardId?.() ?? dashboardService.currentDashboardId;

            if (!dashboardId || !availableDocuments || !availableDocuments.size) return;

            return availableDocuments.get(dashboardId);
        },
        { equal: (a, b) => this.availableDocumentsEqual(a, b) },
    );

    private availableDocumentsEqual(a: AvailableDocuments | undefined, b: AvailableDocuments | undefined): boolean {
        if (a === b) return true;

        if (!a || !b) return false;

        if (a.size !== b.size) return false;

        for (const item of a) {
            if (!b.has(item)) return false;
        }

        return true;
    }

    public async deleteCards(dashboardId: string, cards: string[]) {
        return await this.iframeMessagesService.postMessageResp("deleteCards", { dashboardId, cards }, "cardsDeleted");
    }

    private async getUserAvailableDocuments(productType: string, taxYear: number): Promise<AvailableDocumentsResponse[] | undefined> {
        try {
            const result = await this.iframeMessagesService.postMessageResp(
                "getAvailableDocuments",
                { productType, taxYear },
                "gotiFirmTaxAvailableDocuments",
            );
            if (result.status === "success") return result.response as AvailableDocumentsResponse[];
            else {
                this.responseErrorHandlerService.showPopup({
                    typeLayout: "ERROR",
                    messageCode: "defaultError",
                });
            }
        } catch {
            this.responseErrorHandlerService.showPopup({
                typeLayout: "ERROR",
                messageCode: "defaultError",
            });
        }
    }

    public isCardAvailable(cardId: string): boolean {
        if (!this.dashboardsService.currentDashboardIsFirmTax()) return true;
        return this.currentAvailableDocuments()?.has(cardId) ?? false;
    }

    // Avoid using it all the times because can create problems. Only to be used in a route guard
    public async waitForDocuments(): Promise<void> {
        await this.dashboardsService.getCurrentDashboard();
        if (!this.dashboardsService.currentDashboardIsFirmTax()) return;
        await firstValueFrom(
            this.availableDocuments$.pipe(
                filter((availableDocuments) => availableDocuments !== undefined && availableDocuments !== null),
                take(1),
            ),
        );
    }
}
